/**
 * Merkezi Analytics Servisi — Firebase Analytics üzerinden tüm eventler
 * wizardAnalytics.ts bu modülü kullanmaz (kendi bağımsız yapısı var).
 * Bu modül uygulama genelindeki event'ler içindir.
 */
import { logEvent, setUserProperties, setUserId } from 'firebase/analytics'
import { analytics } from '@/lib/firebase'

type EventParams = Record<string, string | number | boolean | undefined>

/**
 * Genel event logla
 */
async function trackEvent(eventName: string, params?: EventParams) {
  try {
    const fbAnalytics = await analytics
    if (fbAnalytics) {
      logEvent(fbAnalytics, eventName, params)
    }
    if (import.meta.env.DEV) {
    }
  } catch {
    // Sessiz hata — analytics asla app'i kırmamalı
  }
}

/**
 * Sayfa görüntüleme
 */
function trackScreen(screenName: string) {
  trackEvent('screen_view', { screen_name: screenName })
}

/**
 * Kullanıcı kimliğini Analytics'e bağla
 */
async function identifyUser(userId: string) {
  try {
    const fbAnalytics = await analytics
    if (fbAnalytics) {
      setUserId(fbAnalytics, userId)
    }
  } catch {
    // Sessiz
  }
}

/**
 * Kullanıcı özelliklerini ayarla (is_pro, group_count vb.)
 */
async function setProperties(properties: Record<string, string>) {
  try {
    const fbAnalytics = await analytics
    if (fbAnalytics) {
      setUserProperties(fbAnalytics, properties)
    }
  } catch {
    // Sessiz
  }
}

// ============================================
// ÖN TANIMLI EVENT'LER
// ============================================

const Analytics = {
  // Core
  trackEvent,
  trackScreen,
  identifyUser,
  setProperties,

  // Auth
  login: (method: string) => trackEvent('login', { method }),
  signUp: (method: string) => trackEvent('sign_up', { method }),
  logout: () => trackEvent('logout'),

  // Grup
  groupCreated: (memberCount: number, assetType: string) =>
    trackEvent('group_created', { member_count: memberCount, asset_type: assetType }),
  groupJoined: (source: string) =>
    trackEvent('group_joined', { source }),
  groupLeft: (groupStatus: string) =>
    trackEvent('group_left', { group_status: groupStatus }),

  // Ödeme
  paymentDeclared: (amount: number, currency: string) =>
    trackEvent('payment_declared', { amount, currency }),
  paymentConfirmed: () => trackEvent('payment_confirmed'),
  paymentRejected: () => trackEvent('payment_rejected'),

  // Davet
  inviteSent: (method: string) =>
    trackEvent('invite_sent', { share_method: method }),
  inviteAccepted: (source: string) =>
    trackEvent('invite_accepted', { source }),

  // Pro / Reklam
  proUpsellClick: (location: string) =>
    trackEvent('pro_upsell_click', { location }),
  adImpression: (placement: string) =>
    trackEvent('ad_impression', { placement }),

  // Kura
  drawExecuted: (groupId: string, drawType: string, memberCount: number) =>
    trackEvent('draw_executed', { group_id: groupId, draw_type: drawType, member_count: memberCount }),

  // Bildirim İzinleri
  notificationPermissionGranted: () => trackEvent('notification_permission_granted'),
  notificationPermissionDenied: () => trackEvent('notification_permission_denied'),

  // PWA Kurulum
  appInstalled: () => trackEvent('app_installed'),

  // Destek
  supportRequestSent: (subject: string) =>
    trackEvent('support_request_sent', { subject }),

  // Hata
  errorShown: (errorType: string, message: string) =>
    trackEvent('error_shown', { error_type: errorType, error_message: message.slice(0, 100) }),
}

export default Analytics
